/**
 * Tokens TypeScript do design system.
 * Alinhados 1:1 com src/styles/tokens.css e tailwind.config.js.
 * Use estes em código JS quando precisar de cor dinâmica (ex: gráfico,
 * inline style condicional, lib de mapa). Para JSX estático, prefira
 * as classes Tailwind correspondentes.
 */

import type { Database } from '@/types/database';
import type { StatusEstrutura } from '@/domain/estrutura';
import { SEM_FAIXA, SEM_LEITURA, SEM_SINAL,
  FORA_DE_SERVICO, type NivelExibido } from '@/domain/instrumentos/nivel-exibido';

// Reaproveita o enum do banco — fonte de verdade do schema.
export type TarpLevel = Database['public']['Enums']['tarp_level_enum'];

export const LEVEL_UI: Record<
  TarpLevel,
  {
    color: string;
    bg: string;
    border: string;
    icon: string;
    label: string;
    /** classe Tailwind equivalente pra background tinted */
    tw: { text: string; bgTinted: string; border: string };
  }
> = {
  NORMAL: {
    color: '#22c55e',
    bg: 'rgba(34,197,94,.12)',
    border: '#22c55e',
    icon: '✅',
    label: 'Normal',
    tw: { text: 'text-level-normal', bgTinted: 'bg-level-normal/10', border: 'border-level-normal' },
  },
  ATENCAO: {
    color: '#eab308',
    bg: 'rgba(234,179,8,.12)',
    border: '#eab308',
    icon: '⚠️',
    label: 'Atenção',
    tw: { text: 'text-level-atencao', bgTinted: 'bg-level-atencao/10', border: 'border-level-atencao' },
  },
  ALERTA: {
    color: '#f97316',
    bg: 'rgba(249,115,22,.12)',
    border: '#f97316',
    icon: '🔶',
    label: 'Alerta',
    tw: { text: 'text-level-alerta', bgTinted: 'bg-level-alerta/10', border: 'border-level-alerta' },
  },
  EMERGENCIA: {
    color: '#ef4444',
    bg: 'rgba(239,68,68,.12)',
    border: '#ef4444',
    icon: '🚨',
    label: 'Emergência',
    tw: { text: 'text-level-emergencia', bgTinted: 'bg-level-emergencia/10', border: 'border-level-emergencia' },
  },
};

export function levelColor(level: TarpLevel): string {
  return LEVEL_UI[level].color;
}

/**
 * Paleta de EXIBIÇÃO — GUPM-228. Os 4 níveis reais mais "sem leitura".
 *
 * `LEVEL_UI` fica **intacto** de propósito: segue indexado só pelos 4 valores de
 * `TarpLevel`, então quem itera nível (a legenda do mapa, o `SemaphoreCard` via
 * `LEVEL_ORDER`) não passa a mostrar um estado que não é nível, e o guard
 * `nivel in LEVEL_UI` do `MpaActionList` continua recusando o estado novo.
 *
 * Cinza-claro: num fundo escuro (`#0f1729`) lê como ausência de informação sem
 * competir com o amarelo da marca nem sugerir severidade.
 */
export const NIVEL_EXIBIDO_UI: Record<
  NivelExibido,
  (typeof LEVEL_UI)[TarpLevel] & { outline?: boolean }
> = {
  ...LEVEL_UI,
  [SEM_LEITURA]: {
    color: '#cbd5e1',
    bg: 'rgba(203,213,225,.10)',
    border: '#64748b',
    icon: '–',
    label: 'Sem leitura',
    tw: { text: 'text-jf-muted', bgTinted: 'bg-white/5', border: 'border-jf-border' },
  },
  /**
   * GUPM-247 — sem faixa de controle. Distingue-se do "sem leitura" por **forma**,
   * não só por matiz: `outline` marca o estado como vazado (pino sem preenchimento,
   * borda tracejada no badge). Duas razões: acessibilidade — diferença só de cor
   * falha WCAG 1.4.1 — e o manual da marca JF Brasil, cujo dispositivo de distinção
   * é justamente `fill × outline` ("vazada 3 px"). Nenhum tom de amarelo entra aqui:
   * o amarelo é acento único da marca e não codifica status.
   *
   * `#94a3b8` = `jf-muted`, tint navy intermediário: legível sobre `#0f1729` e
   * claramente mais escuro que o `#cbd5e1` do "sem leitura".
   */
  [SEM_FAIXA]: {
    color: '#94a3b8',
    bg: 'transparent',
    border: '#94a3b8',
    icon: '○',
    label: 'Sem limite definido',
    tw: { text: 'text-jf-muted', bgTinted: 'bg-transparent', border: 'border-jf-muted' },
    outline: true,
  },
  /**
   * GUPM-303 — sem sinal, no matiz do **ALERTA**. Não é escolha de cor: o catálogo
   * declara `TARP-PS-TILT` com `nivel: 'ALERTA'` e o bloco "Precisa da sua atenção" já
   * ranqueia sem-sinal como `SEV_RANK.ALERTA`. A tabela usar outro matiz punha três
   * lugares da mesma tela discordando sobre a gravidade do mesmo sensor.
   *
   * A primeira versão deste token era um âmbar próprio (`#d4a15a`), pelo receio de que
   * reusar cor de nível fizesse a tela afirmar gravidade CLASSIFICADA. O receio vale
   * contra o `#dc2626` da EMERGENCIA — vermelho manda o RT para o caminho de evacuação,
   * quando a ação aqui é telemetria e campo — mas não contra o ALERTA, que é
   * exatamente a severidade que o protocolo atribui. O âmbar dizia MENOS do que o
   * protocolo, e num sensor de ruptura errar para menos é o pior lado.
   *
   * A distinção para o alerta medido fica por FORMA e RÓTULO (WCAG 1.4.1): ícone `⃠`
   * contra `🔶`, rótulo "Sem sinal" contra "Alerta", e `outline: true` mantendo o pino
   * vazado no mapa. Nunca só por matiz.
   */
  [SEM_SINAL]: {
    color: '#f97316',
    bg: 'rgba(249,115,22,.12)',
    border: 'rgba(249,115,22,.45)',
    icon: '⃠',
    label: 'Sem sinal',
    tw: { text: 'text-level-alerta', bgTinted: 'bg-level-alerta/10', border: 'border-level-alerta' },
    outline: true,
  },
  /**
   * GUPM-307 — fora de serviço declarado, em CINZA e vazado.
   *
   * O oposto deliberado do `SEM_SINAL`, que é laranja de ALERTA. Aqui a gravidade é
   * zero: alguém já sabe, já declarou e já está tratando. Pintar isso com matiz de
   * severidade seria pedir ação para trabalho em curso — e é justamente o alarme falso
   * que este card remove do `INA-12A`.
   *
   * Mesmo `#94a3b8` do `SEM_FAIXA` (`jf-muted`): os dois são "a tela não afirma nível
   * aqui". A distinção entre eles é por ÍCONE e RÓTULO, nunca por matiz (WCAG 1.4.1) —
   * `⏸` contra `○`, "Fora de serviço" contra "Sem limite definido". `outline: true`
   * mantém o pino vazado no mapa, coerente com os outros estados sem lastro.
   */
  [FORA_DE_SERVICO]: {
    color: '#94a3b8',
    bg: 'transparent',
    border: '#94a3b8',
    icon: '⏸',
    label: 'Fora de serviço',
    tw: { text: 'text-jf-muted', bgTinted: 'bg-transparent', border: 'border-jf-muted' },
    outline: true,
  },
};

export function nivelExibidoColor(nivel: NivelExibido): string {
  return NIVEL_EXIBIDO_UI[nivel].color;
}

/**
 * Cor de identidade por barragem (A/B/C) — canônica no app inteiro (dashboard,
 * mapa, TARP, chip do M6). Espelha `damColors` do protótipo V8. Fallback neutro
 * (cinza-azulado) para código desconhecido / "Todas".
 */
export const DAM_COLOR: Record<string, string> = {
  A: '#a78bfa', // violeta/lilás
  B: '#38bdf8', // azul-céu
  C: '#e879a6', // rosa
};

export function damColor(code: string): string {
  return DAM_COLOR[code] ?? '#94a3b8';
}

/**
 * UI dos 6 estados de STATUS DA ESTRUTURA (Barragem A/B/C) — espelha `STATUS_UI`
 * do protótipo v5.10 (docs/reference/GeoGuard_UPM_v5_10_0_8.html, L1681-1688).
 *
 * Distinto de `LEVEL_UI` (TarpLevel, 4 níveis): inclui OBSERVACAO (nível de
 * controle PRÉVIO à ANM, azul) e CRITICO. Usado pelos chips A/B/C do Header.
 */
export const STATUS_ESTRUTURA_UI: Record<
  StatusEstrutura,
  { label: string; color: string; bg: string; border: string; icon: string }
> = {
  NORMAL:     { label: 'Normal',     color: '#22c55e', bg: 'rgba(34,197,94,.12)',  border: 'rgba(34,197,94,.4)',  icon: '✅' },
  OBSERVACAO: { label: 'Observação', color: '#38bdf8', bg: 'rgba(56,189,248,.12)', border: 'rgba(56,189,248,.45)', icon: '🔎' },
  ATENCAO:    { label: 'Atenção',    color: '#eab308', bg: 'rgba(234,179,8,.12)',  border: 'rgba(234,179,8,.45)', icon: '⚠️' },
  ALERTA:     { label: 'Alerta',     color: '#f97316', bg: 'rgba(249,115,22,.12)', border: 'rgba(249,115,22,.45)', icon: '🔶' },
  CRITICO:    { label: 'Crítico',    color: '#ef4444', bg: 'rgba(239,68,68,.12)',  border: 'rgba(239,68,68,.5)',  icon: '🛑' },
  EMERGENCIA: { label: 'Emergência', color: '#dc2626', bg: 'rgba(220,38,38,.16)',  border: 'rgba(220,38,38,.6)',  icon: '🚨' },
};

/**
 * Cor do TÍTULO do card de anomalia — tom claro do V8 (`crit-card .cc-title`).
 *
 * Morava dentro do `AnomalyList.tsx`. Veio para cá no GUPM-303 por dois motivos: é
 * tabela de cor, e este arquivo é onde as tabelas de cor vivem; e exportá-la de um
 * arquivo de componente quebra o Fast Refresh (`react-refresh/only-export-components`).
 */
export const TITLE_COLOR: Record<TarpLevel, string> = {
  EMERGENCIA: '#fca5a5',
  ALERTA: '#fdba74',
  ATENCAO: '#fde047',
  NORMAL: '#93c5e8',
};

/**
 * Título do card de perda de sinal. **Referência, não literal.**
 *
 * Era `#e8c48f`, o claro do âmbar `#d4a15a` que o token usava antes — e ficou preso lá
 * quando `NIVEL_EXIBIDO_UI[SEM_SINAL]` passou para o laranja do ALERTA: o card ganhou
 * fundo, borda, ícone e botão laranja, e um título âmbar que não pareava com mais nada
 * na tela. Era a quarta cópia da mesma cor, a única sem nada amarrando-a às outras.
 *
 * Apontar para `TITLE_COLOR.ALERTA` mata a cópia: o sem sinal usa o matiz do ALERTA
 * porque o catálogo dá esse nível ao `TARP-PS-TILT`. A distinção entre os dois cards
 * continua por ícone e rótulo, nunca por matiz.
 */
export const TITLE_COLOR_SEM_SINAL = TITLE_COLOR.ALERTA;

export const RADIUS = {
  sm: 8,
  md: 12,
  lg: 16,
  pill: 20,
} as const;
